import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError } from '@/api/client';
import { useSession } from '@/stores/session';
import { Tag, Dot } from '@/components/Tag';
import { Empty } from '@/components/States';
import { date, dateTime, money, num, qty } from '@/lib/format';
import { DetailError, DetailLoading, DetailShell, KV, Panel, detailStyles as d } from '@/features/products/kit/Detail';
import { useResource } from '@/features/products/kit/hooks';
import { activeState, purchaseStatus, quoteSource, settlementText } from './status';
import type { Supplier, SupplierPurchaseOrder, SupplierPurchases } from './types';
import s from './PartnerDetail.module.css';

type Tab = 'orders' | 'in' | 'quotes';

/** 已入库 / 未入库货值：按明细 已收 × 单价、(订购 − 已收) × 单价 累加（老版 suppliers/[id]/purchases） */
function receivedValue(po: SupplierPurchaseOrder) {
  return po.lines.reduce((a, l) => a + num(l.receivedQuantity) * num(l.unitCost), 0);
}
function pendingValue(po: SupplierPurchaseOrder) {
  return po.lines.reduce((a, l) => a + Math.max(num(l.quantity) - num(l.receivedQuantity), 0) * num(l.unitCost), 0);
}

/** 采购记录（老版「采购记录」页）：需要 purchase.read，各取最近 80 / 120 / 120 条 */
function Purchases({ id }: { id: string }) {
  const [tab, setTab] = useState<Tab>('orders');
  const path = `/api/suppliers/${id}/purchases`;
  const { data, loading, error, reload } = useResource<SupplierPurchases>(path);
  const tabs: [Tab, string, number | undefined][] = [
    ['orders', '采购单', data?.purchaseOrders.length],
    ['in', '入库流水', data?.inventoryTransactions.length],
    ['quotes', '报价历史', data?.quoteHistories.length],
  ];
  return (
    <Panel title="采购记录" note="最近的记录；点击单据查看详情" flush>
      <div className={s.tabs} role="tablist">
        {tabs.map(([k, label, n]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? s.on : ''} onClick={() => setTab(k)}>
            {label}{n != null && <i>{n}</i>}
          </button>
        ))}
      </div>
      {error ? (
        <DetailError title="采购记录加载失败" error={error} path={path} onRetry={reload} />
      ) : loading || !data ? (
        <p className={d.none}>加载中…</p>
      ) : tab === 'orders' ? (
        data.purchaseOrders.length ? (
          <div className={s.scrollx}>
            <table className={d.tbl}>
              <thead><tr><th>采购单号</th><th>状态</th><th className={s.hideNarrow}>采购日期</th><th className={s.hideNarrow}>仓库</th><th className={d.r}>采购金额</th><th className={`${d.r} ${s.hideNarrow}`}>已入库货值</th><th className={d.r}>未入库货值</th></tr></thead>
              <tbody>
                {data.purchaseOrders.map((po) => {
                  const st = purchaseStatus[po.status] || { label: po.status, tone: 'none' as const };
                  const pend = pendingValue(po);
                  return (
                    <tr key={po.id}>
                      <td><Link className={`${s.link} tnum`} to={`/purchase-orders/${po.id}`}>{po.purchaseNo}</Link></td>
                      <td><Tag tone={st.tone} sm>{st.label}</Tag></td>
                      <td className={`tnum ${s.hideNarrow}`}>{date(po.orderDate)}</td>
                      <td className={s.hideNarrow}>{po.warehouse.name}</td>
                      <td className={d.r}>{money(po.totalAmount)}</td>
                      <td className={`${d.r} ${s.hideNarrow}`}>{money(receivedValue(po))}</td>
                      <td className={d.r}>{pend > 0 ? money(pend) : <span className="em">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <p className={d.none}>还没有采购单。</p>
      ) : tab === 'in' ? (
        data.inventoryTransactions.length ? (
          <div className={s.scrollx}>
            <table className={d.tbl}>
              <thead><tr><th>时间</th><th>产品</th><th className={s.hideNarrow}>仓库</th><th className={s.hideNarrow}>采购单</th><th className={d.r}>数量</th><th className={d.r}>单价</th></tr></thead>
              <tbody>
                {data.inventoryTransactions.map((t) => (
                  <tr key={t.id}>
                    <td className="tnum">{dateTime(t.createdAt)}</td>
                    <td><Link className={s.link} to={`/products/${t.product.id}`}>{t.product.name}</Link><small className="tnum">{t.product.code}</small></td>
                    <td className={s.hideNarrow}>{t.warehouse.name}</td>
                    <td className={`tnum ${s.hideNarrow}`}>{t.purchaseOrder?.purchaseNo || '—'}</td>
                    <td className={d.r}>{qty(t.quantity)}</td>
                    <td className={d.r}>{t.unitCost != null ? money(t.unitCost) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className={d.none}>还没有从这个供应商入库的记录。</p>
      ) : data.quoteHistories.length ? (
        <div className={s.scrollx}>
          <table className={d.tbl}>
            <thead><tr><th>时间</th><th>产品 / 包装方案</th><th className={d.r}>报价</th><th className={s.hideNarrow}>来源</th><th className={s.hideNarrow}>操作人</th></tr></thead>
            <tbody>
              {data.quoteHistories.map((h) => (
                <tr key={h.id}>
                  <td className="tnum">{dateTime(h.createdAt)}</td>
                  <td>
                    <Link className={s.link} to={`/products/${h.packagingOption.product.id}`}>{h.packagingOption.product.name}</Link>
                    <small>{h.packagingOption.packageName || h.packagingOption.packageMethod || h.packagingOption.description || '—'}{h.isDefault ? ' · 默认报价' : ''}</small>
                  </td>
                  <td className={d.r}>{money(h.quotePrice)}</td>
                  <td className={s.hideNarrow}>{quoteSource[h.source || ''] || h.source || '—'}</td>
                  <td className={s.hideNarrow}>{h.createdBy?.name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className={d.none}>还没有报价记录。</p>}
    </Panel>
  );
}

export function SupplierDetail({ id, embedded }: { id: string; embedded?: boolean }) {
  const can = useSession((st) => st.can);
  const path = `/api/suppliers/${id}`;
  const { data: v, loading, error, reload } = useResource<Supplier>(path);

  if (error instanceof ApiError && error.status === 404) {
    return <Empty icon="info" title="供应商不存在" action={<Link className={s.back} to="/suppliers">回供应商列表</Link>}>可能已被删除，或者链接有误。</Empty>;
  }
  if (error) return <DetailError title="供应商加载失败" error={error} path={path} onRetry={reload} />;
  if (loading || !v) return <DetailLoading />;

  const st = activeState(v.isActive);
  const stl = settlementText(v);

  const main = (
    <>
      <Panel title="联系信息">
        <KV
          rows={[
            ['供应商编号', v.code ? <span className="tnum">{v.code}</span> : null],
            ['类型', v.supplierType || null],
            ['联系人', v.contact || null],
            ['电话', v.phone ? <a className={`${s.tel} tnum`} href={`tel:${v.phone}`}>{v.phone}</a> : null],
            ['地址', v.address || null],
            ['备注', v.note ? <span className={s.pre}>{v.note}</span> : null],
          ]}
        />
      </Panel>
      {can('purchase.read') ? <Purchases id={v.id} /> : <p className={s.hint}>采购记录需要「采购 · 查看」权限。</p>}
    </>
  );

  const aside = (
    <>
      <Panel title="结算">
        <KV
          rows={[
            ['结算方式', <Dot tone={stl.dot}>{v.isMonthlySettlement ? '月结' : '非月结'}</Dot>],
            ['结算日', v.settlementDay ? `每月 ${v.settlementDay} 日` : null],
            ['账期', v.paymentTermDays != null ? `${v.paymentTermDays} 天` : null],
          ]}
        />
      </Panel>
      <Panel title="记录">
        <KV rows={[['创建', <span className="tnum">{dateTime(v.createdAt)}</span>], ['更新', <span className="tnum">{dateTime(v.updatedAt)}</span>]]} />
      </Panel>
    </>
  );

  return (
    <DetailShell
      embedded={embedded}
      backTo="/suppliers"
      title={v.name}
      badges={st && <Tag tone={st.tone}>{st.label}</Tag>}
      openHref={`/suppliers/${v.id}`}
      actions={can('suppliers.write') && <Link className={s.back} to={`/suppliers/${v.id}/edit`}>编辑</Link>}
      sub={<>{v.code || '无编号'} · {v.supplierType || '未分类'} · {stl.label}</>}
      main={main}
      aside={aside}
    />
  );
}
